import { Fragment, type ReactNode } from "react";
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  type Row,
  useReactTable,
} from "@tanstack/react-table";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/ui/table";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";

/**
 * Người ta đang bấm vào một thứ bấm được nằm TRONG dòng (nút, liên kết, ô
 * nhập, ô chọn)? Nếu có thì để yên cho thứ đó, đừng chọn dòng đè lên.
 */
const bamVaoThuKhac = (target: EventTarget | null): boolean =>
  target instanceof Element &&
  !!target.closest("button, a, input, select, textarea, [role='button'], [role='menuitem']");

type MobilePriority = "primary" | "secondary" | "hidden";

declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData, TValue> {
    mobilePriority?: MobilePriority;
    mobileLabel?: string;
  }
}

/**
 * Nhóm của một dòng. `id` chỉ dùng để biết dòng nào mở nhóm mới; `header` là
 * thứ vẽ ra trên dải tiêu đề nhóm.
 */
export type RowGroup = { id: string; header: ReactNode };

type Props<TData, TValue> = {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  emptyMessage?: string;
  mobileVariant?: "scroll" | "stack";
  /**
   * Chia bảng thành nhóm có dải tiêu đề, KHÔNG sắp lại dòng: dải mới được chèn
   * mỗi khi `id` khác dòng liền trước. Bỏ trống = bảng phẳng y như trước.
   *
   * Vì sao nằm ở đây mà không phải mỗi nhóm một bảng riêng: mỗi `<table>` tự
   * tính độ rộng cột theo dữ liệu CỦA NÓ, nên xếp chồng nhiều bảng cùng bộ cột
   * thì các cột lệch nhau — đo 01/09/2026 trên sổ hậu mãi: cột "Loại đơn" lệch
   * 36px, "Cơ sở" lệch 30px giữa ba nhóm, nhìn như bảng vỡ.
   */
  groupBy?: (row: TData) => RowGroup;
  /**
   * Bấm chỗ nào trong dòng cũng chọn được dòng đó, không phải nhắm đúng chữ.
   *
   * Dòng vẫn KHÔNG phải là nút: cột nào cần bàn phím thì tự đặt nút thật bên
   * trong ô (xem cột "Khách" ở màn Hàng in). Ở đây chỉ bắt tiếng bấm chuột,
   * và bỏ qua khi người ta đang bấm vào nút/liên kết/ô nhập nằm trong dòng —
   * bấm nút xoá mà lại chọn luôn dòng là hành vi bất ngờ.
   */
  onRowClick?: (row: TData) => void;
  /** Dòng nào đang được chọn — để tô nền. Bỏ trống = không tô. */
  isRowSelected?: (row: TData) => boolean;
  /**
   * Lớp riêng theo dữ liệu của từng dòng (cả hàng bảng lẫn thẻ dọc trên điện
   * thoại) — vd tô dòng thông báo chưa đọc. Dòng đang chọn vẫn ưu tiên màu
   * chọn. Bỏ trống = dòng vẽ như cũ.
   */
  rowClassName?: (row: TData) => string | undefined;
};

/** StackCard — một dòng dưới dạng thẻ dọc trên điện thoại. */
function StackCard<TData>({
  row,
  onRowClick,
  selected,
  extraClass,
}: {
  row: Row<TData>;
  onRowClick: ((row: TData) => void) | undefined;
  selected: boolean;
  extraClass: string | undefined;
}) {
  const primaryCells = row
    .getVisibleCells()
    .filter((c) => c.column.columnDef.meta?.mobilePriority === "primary");
  const secondaryCells = row.getVisibleCells().filter((c) => {
    const p = c.column.columnDef.meta?.mobilePriority;
    return p !== "primary" && p !== "hidden";
  });

  return (
    <div
      className={cn(
        "rounded-lg border border-border bg-surface p-3 shadow-sm",
        onRowClick && "cursor-pointer",
        extraClass,
        selected && "border-primary bg-primary/5",
      )}
      onClick={
        onRowClick
          ? (e) => {
              if (!bamVaoThuKhac(e.target)) onRowClick(row.original);
            }
          : undefined
      }
    >
      {primaryCells.length > 0 && (
        // Cho xuống dòng: màn Đơn in đánh dấu 5 cột là "chính", năm cột ép trên
        // MỘT hàng ở máy 390px thì hàng rộng hơn màn và đẩy cả trang trượt
        // ngang (đo 26/09/2026). Xuống dòng giữ đủ thông tin mà vẫn vừa màn.
        <div className="mb-1 flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
          {primaryCells.map((c) => (
            <span key={c.id} className="min-w-0 text-sm font-semibold">
              {flexRender(c.column.columnDef.cell, c.getContext())}
            </span>
          ))}
        </div>
      )}
      {secondaryCells.map((c) => {
        const label =
          c.column.columnDef.meta?.mobileLabel ??
          (typeof c.column.columnDef.header === "string" ? c.column.columnDef.header : "");
        return (
          <div key={c.id} className="flex items-baseline justify-between gap-2 py-0.5 text-xs">
            {label && <span className="shrink-0 text-muted-foreground">{label}</span>}
            {/* `min-w-0` mới là chỗ quyết định: mặc định một ô flex không co
             * xuống dưới bề rộng nội dung của nó, nên một giá trị dài (ngày +
             * nhãn trạng thái + mã cơ sở) đẩy cả thẻ rộng ra — màn Đơn in vì
             * thế trượt ngang 32px trên máy 390px (đo 26/09/2026). Cho phép co
             * rồi xuống dòng thì thẻ luôn vừa bề ngang màn. */}
            <span className="min-w-0 break-words text-right">
              {flexRender(c.column.columnDef.cell, c.getContext())}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export function DataTable<TData, TValue>({
  columns,
  data,
  emptyMessage = "Không có dữ liệu",
  mobileVariant = "scroll",
  groupBy,
  onRowClick,
  isRowSelected,
  rowClassName,
}: Props<TData, TValue>) {
  const table = useReactTable({ columns, data, getCoreRowModel: getCoreRowModel() });
  const isMobile = useIsMobile();
  const useStack = mobileVariant === "stack" && isMobile;

  /** Dòng thứ i có mở một nhóm mới không (so với dòng liền trước)? */
  const opensGroupAt = (i: number, group: RowGroup | undefined): boolean =>
    !!group &&
    (i === 0 || groupBy?.(table.getRowModel().rows[i - 1]!.original).id !== group.id);

  if (useStack) {
    return (
      <div className="space-y-2">
        {data.length === 0 ? (
          <div className="rounded-md border bg-surface p-6 text-center text-muted">{emptyMessage}</div>
        ) : (
          table.getRowModel().rows.map((row, i) => {
            const group = groupBy?.(row.original);
            return (
              <Fragment key={row.id}>
                {opensGroupAt(i, group) && (
                  <div className="px-1 pt-2 text-xs text-muted-foreground">{group!.header}</div>
                )}
                <StackCard
                  row={row}
                  onRowClick={onRowClick}
                  selected={!!isRowSelected?.(row.original)}
                  extraClass={rowClassName?.(row.original)}
                />
              </Fragment>
            );
          })
        )}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-md border bg-surface">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((hg) => (
            <TableRow key={hg.id}>
              {hg.headers.map((h) => (
                <TableHead key={h.id}>
                  {h.isPlaceholder ? null : flexRender(h.column.columnDef.header, h.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center text-muted">
                {emptyMessage}
              </TableCell>
            </TableRow>
          ) : (
            table.getRowModel().rows.map((row, i) => {
              const group = groupBy?.(row.original);
              return (
                <Fragment key={row.id}>
                  {opensGroupAt(i, group) && (
                    <TableRow className="hover:bg-transparent">
                      <TableCell
                        colSpan={columns.length}
                        className="bg-muted/50 py-1.5 text-xs text-muted-foreground"
                      >
                        {group!.header}
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow
                    className={cn(
                      onRowClick && "cursor-pointer",
                      rowClassName?.(row.original),
                      isRowSelected?.(row.original) && "bg-primary/5",
                    )}
                    onClick={
                      onRowClick
                        ? (e) => {
                            if (!bamVaoThuKhac(e.target)) onRowClick(row.original);
                          }
                        : undefined
                    }
                  >
                    {row.getVisibleCells().map((c) => (
                      <TableCell key={c.id}>{flexRender(c.column.columnDef.cell, c.getContext())}</TableCell>
                    ))}
                  </TableRow>
                </Fragment>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
}
